import type { Observable, Subscription } from "rxjs";
import { of as observableOf, throwError as observableThrowError } from "rxjs";

import type { ILogService, jwt } from "angular";
import { concat, delay, publishReplay, refCount, switchMap, tap } from "rxjs/operators";

import "angular-jwt";
import "angular-local-storage";

import { parseZone } from "moment-timezone";

import "../../Repositories/Token/TokenRepository";

import type { ITokenHolderService, ITokenService, ITokenRepository } from "App/Modules/Auth";

import type { ILogin, IResponse, IToken } from "App/Modules/Models";
import { IrisitySystems } from "App/Modules/Models";

export class TokenService implements ITokenService {
    public static $inject = ["$log", "mmTokenHolderService", "mmInternalTokenRepository", "jwtHelper"];

    private lastToken: IToken = null;

    private tokenRepositorySubscription: Subscription;

    private tokenObservableInstance: Observable<IToken>;

    constructor(
        private readonly $log: ILogService,
        private readonly tokenHolderService: ITokenHolderService,
        private readonly tokenRepository: ITokenRepository,
        private readonly jwtHelper: angular.jwt.IJwtHelper,
    ) {
        this.tokenObservableInstance = this.initTokenObservable();

        this.tokenObservable().subscribe((token: IToken) => {
            this.lastToken = token;
        });
    }

    public tokenObservable(): Observable<IToken> {
        return this.tokenObservableInstance;
    }

    public getAccessTokenIfValid(): string {
        return this.lastToken &&
            this.lastToken.accessToken &&
            !this.jwtHelper.isTokenExpired(this.lastToken.accessToken)
            ? this.lastToken.accessToken
            : null;
    }

    public refreshAccessToken(): Observable<IResponse<IToken>> {
        if (this.lastToken && this.lastToken.accessToken) {
            this.$log.info("TokenService.RefreshAccessToken: Access token already valid, no need to refresh.");
            return observableOf<IResponse<IToken>>({
                data: this.lastToken,
                hasError: false,
            });
        }
        if (this.lastToken && this.lastToken.refreshToken) {
            this.$log.info("TokenService.RefreshAccessToken: Refreshing access token...");
            return this.tokenRepository.put(this.lastToken.refreshToken).pipe(
                tap(
                    (response) => {
                        this.tokenHolderService.setToken(response.data);
                    },
                    () => {
                        this.tokenHolderService.deleteToken();
                    },
                ),
            );
        }
        const errorMessage = "Refresh token does not exist - cannot refresh access token!";
        this.$log.warn(`TokenService.RefreshAccessToken: ${errorMessage}`);
        this.tokenHolderService.deleteToken();

        // This is not consistent with IResponse, and error is also non-translatable
        const error: IResponse<void> = {
            error: errorMessage as any,
            hasError: true,
        };
        return observableThrowError(error);
    }

    public login(login: ILogin): Observable<IResponse<IToken>> {
        return this.tokenRepository.post(login).pipe(
            tap((response: IResponse<IToken>) => {
                if (
                    response.data.validFor.some((is) => is === IrisitySystems.ManagePortal) ||
                    response.data.validFor.some((is) => is === IrisitySystems.CarePortal)
                ) {
                    this.tokenHolderService.setToken(response.data);
                } else {
                    response.status = 401;
                }
            }),
        );
    }

    public logout(): void {
        this.tokenHolderService.deleteToken();
    }

    private initTokenObservable(): Observable<IToken> {
        return this.tokenHolderService.tokenObservable().pipe(
            switchMap((token: IToken) => {
                if (token && token.accessToken) {
                    if (this.jwtHelper.isTokenExpired(token.accessToken)) {
                        token.accessToken = null;
                    } else {
                        token.accessTokenExpiry = parseZone(this.jwtHelper.getTokenExpirationDate(token.accessToken));
                        const fullToken: jwt.JwtToken = this.jwtHelper.decodeToken(token.accessToken);
                        token.email = (fullToken as any).email;
                        token.issuer = fullToken.iss;
                    }
                }

                let observable: Observable<IToken> = observableOf(token);

                if (token && token.accessTokenExpiry) {
                    const expiredToken: IToken = { ...token, accessToken: null } as IToken;
                    observable = observable.pipe(
                        concat(observableOf(expiredToken).pipe(delay(token.accessTokenExpiry.toDate()))),
                    );
                }
                return observable;
            }),
            publishReplay(1),
            refCount(),
        );
    }
}
